import React from 'react';
import { DataAPIContext, DELETE, GET_ONE } from '../../api';
import { UPLOAD } from '../../api/dataProvider';
import { isOk } from '../../utils/errors';

// CP-7151: custom map pin icons are a library of the tour's operator, shared by all of its tours
// (backend `tour/<id>/map_pin_icons/`: GET lists it, PUT uploads into it, DELETE <icon id> takes an
// icon out). Every stop of the trip has its own picker, so one copy per trip lives here, loaded once
// and refreshed whenever a picker opens.
const libraries = new Map(); // trip id -> [{ id, name, url }]
const loading = new Map(); // trip id -> the request in flight
const listeners = new Set();

const resource = (tripId) => `tour/${tripId}/map_pin_icons`;

const publish = (tripId, icons) => {
    libraries.set(tripId, icons);
    listeners.forEach((listener) => listener(tripId));
};

const fetchLibrary = (dataAPI, tripId) => {
    if (!loading.has(tripId)) {
        const request = dataAPI(GET_ONE, 'tour', { id: tripId, urlSuffix: 'map_pin_icons/' })
            .then((res) => {
                if (isOk(res) && Array.isArray(res.data?.results)) publish(tripId, res.data.results);
            })
            .catch(() => { /* the picker keeps what it had */ })
            .finally(() => loading.delete(tripId));
        loading.set(tripId, request);
    }
    return loading.get(tripId);
};

// Tests only: the library is module state shared by every picker.
export const resetMapPinIconLibraries = () => {
    libraries.clear();
    loading.clear();
};

export default function useMapPinIconLibrary(tripId) {
    const dataAPI = React.useContext(DataAPIContext);
    const [icons, setIcons] = React.useState(() => libraries.get(tripId) || []);

    React.useEffect(() => {
        const onChange = (id) => {
            if (id === tripId) setIcons(libraries.get(tripId) || []);
        };
        listeners.add(onChange);
        setIcons(libraries.get(tripId) || []);
        if (tripId && !libraries.has(tripId)) fetchLibrary(dataAPI, tripId);
        return () => listeners.delete(onChange);
    }, [dataAPI, tripId]);

    const refresh = React.useCallback(() => (tripId ? fetchLibrary(dataAPI, tripId) : Promise.resolve()), [dataAPI, tripId]);

    // Resolves to the data provider's response; on success the icon is in the library.
    const upload = React.useCallback(async (file) => {
        if (!tripId) {
            // a trip that was never saved has no library to add to
            return dataAPI(UPLOAD, 'map_pin_icon', { data: { file }, urlSuffix: 'upload/' });
        }
        const res = await dataAPI(UPLOAD, resource(tripId), { data: { file } });
        if (isOk(res) && res.data?.id && res.data?.url) {
            const icon = { id: res.data.id, name: res.data.name, url: res.data.url };
            publish(tripId, [...(libraries.get(tripId) || []).filter((i) => i.url !== icon.url), icon]);
        }
        return res;
    }, [dataAPI, tripId]);

    const remove = React.useCallback(async (icon) => {
        const res = await dataAPI(DELETE, resource(tripId), { id: icon.id });
        if (isOk(res)) {
            publish(tripId, (libraries.get(tripId) || []).filter((i) => i.id !== icon.id && i.url !== icon.url));
        }
        return res;
    }, [dataAPI, tripId]);

    return { icons, refresh, upload, remove };
}
